import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableGrid from "../Components/Restaurant/TableGrid";
import RestaurantMenu from "../Components/Restaurant/RestaurantMenu";
import OrderPanel from "../Components/Restaurant/OrderPanel";
import OnlineOrderButton from "../Components/Restaurant/OnlineOrderButton";
import PhoneOrderButton from "../Components/Restaurant/PhoneOrderButton";
import ReservationButton from "../Components/Restaurant/ReservationDialog";
import CustomerDialog from "../Components/Customer/CustomerDialog";
import CustomizableWorkspace from "../Components/Restaurant/CustomizableWorkspace";
import SalesSummaryOverlay from "../Components/Shared/SalesSummaryOverlay";
import {
  normalizeTablePayload,
  selectSelectedTableId,
  selectTables,
  setRestaurantTables,
} from "../Store/Slices/restaurantSlice";
import { useGetAllTablesQuery } from "../Store/Services/TableService";
import { syncTableCartIdsFromTables } from "../Utils/cartSession";
import styles from "../Components/Restaurant/Restaurant.module.css";

const RestaurantPage = () => {
  const dispatch = useDispatch();
  const selectedTableId = useSelector(selectSelectedTableId);
  const tables = useSelector(selectTables);
  // Table availability changes constantly from other terminals, so re-fetch on
  // every visit instead of showing whatever is left in the RTK Query cache.
  const { data: tableData } = useGetAllTablesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileTab, setMobileTab] = useState("menu");

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 640px)");
    const updateLayoutMode = () => setIsMobileView(mediaQuery.matches);
    updateLayoutMode();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateLayoutMode);
      return () => mediaQuery.removeEventListener("change", updateLayoutMode);
    }
    mediaQuery.addListener(updateLayoutMode);
    return () => mediaQuery.removeListener(updateLayoutMode);
  }, []);

  useEffect(() => {
    if (!tableData) return;

    const payload = normalizeTablePayload(tableData);

    if (
      payload.tableTypes.length > 0 ||
      payload.phoneOrders.length > 0 ||
      payload.onlineOrders.length > 0
    ) {
      dispatch(setRestaurantTables(payload));
      syncTableCartIdsFromTables(payload);
    }
  }, [tableData, dispatch]);

  const selectedTable = useMemo(
    () => tables.find((t) => t.id === selectedTableId),
    [tables, selectedTableId],
  );

  useEffect(() => {
    setMobileTab("menu");
  }, [selectedTableId]);

  const widgets = useMemo(
    () => [
      {
        key: "tables",
        title: "Tables",
        node: <TableGrid />,
        defaultSpan: 12,
        defaultDock: "top",
        visible: true,
      },
      {
        key: "menu",
        title: "Menu",
        node: selectedTable ? <RestaurantMenu tableId={selectedTable.id} /> : null,
        defaultSpan: 8,
        defaultDock: "left",
        visible: Boolean(selectedTableId && selectedTable),
      },
      {
        key: "order",
        title: "Order",
        node: selectedTable ? <OrderPanel table={selectedTable} /> : null,
        defaultSpan: 4,
        defaultDock: "right",
        visible: Boolean(selectedTableId && selectedTable),
      },
    ],
    [selectedTable, selectedTableId],
  );

  const presets = useMemo(
    () => [
      {
        key: "three-column",
        label: "3 Columns",
        layout: {
          tables: { order: 0, span: 2, dock: "left" },
          menu: { order: 1, span: 8, dock: "middle" },
          order: { order: 2, span: 4, dock: "right" },
        },
      },
      {
        key: "top-stacked",
        label: "Tables on Top",
        layout: {
          tables: { order: 0, span: 12, dock: "top" },
          menu: { order: 1, span: 8, dock: "left" },
          order: { order: 2, span: 4, dock: "right" },
        },
      },
    ],
    [],
  );

  const summary = useMemo(() => <SalesSummaryOverlay variant="inline" />, []);
  const desktopActions = useMemo(
    () => (
      <>
        <OnlineOrderButton />
        <PhoneOrderButton sx={{ ml: 1 }} />
        <ReservationButton sx={{ ml: 1 }} />
      </>
    ),
    [],
  );

  return (
    <div className={styles.page}>
      {isMobileView ? (
        <div className={styles.mobileStack}>
          <div className={styles.mobileActions}>
            <OnlineOrderButton />
            <PhoneOrderButton />
            <ReservationButton />
          </div>
          <TableGrid />
          {selectedTable && (
            <>
              <div className={styles.mobileTabs}>
                <button
                  type="button"
                  className={`${styles.mobileTab} ${
                    mobileTab === "menu" ? styles.mobileTabActive : ""
                  }`}
                  onClick={() => setMobileTab("menu")}
                >
                  Menu
                </button>
                <button
                  type="button"
                  className={`${styles.mobileTab} ${
                    mobileTab === "order" ? styles.mobileTabActive : ""
                  }`}
                  onClick={() => setMobileTab("order")}
                >
                  Order Details
                </button>
              </div>
              {mobileTab === "menu" ? (
                <RestaurantMenu tableId={selectedTable.id} />
              ) : (
                <OrderPanel table={selectedTable} />
              )}
            </>
          )}
        </div>
      ) : (
        <CustomizableWorkspace
          storageKey="posRestaurantLayout"
          widgets={widgets}
          presets={presets}
          summary={summary}
          actions={desktopActions}
        />
      )}
      <CustomerDialog />
    </div>
  );
};

export default RestaurantPage;
